import { yupResolver } from '@hookform/resolvers/yup';
import {
	Box,
	Button,
	FormControl,
	FormHelperText,
	Grid,
	InputAdornment,
	TextField,
	Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { resetPassword } from 'store/user-service/asyncActions';
import { getUser } from 'store/user-service/selector';

import { NamespacesList } from 'core/enums/common';
import { resetPasswordSchema } from 'core/helpers/yupSchemas';

import { ReactComponent as EyeSlash } from 'assets/icons/eye-slash.svg';
import { ReactComponent as Eye } from 'assets/icons/eye.svg';
import { ReactComponent as Unlock } from 'assets/icons/unlock.svg';

import { ResetSuccess } from './ResetSuccess';

export const ResetPasswordForm = () => {
	const { t: resetPasswordTranslate } = useTranslation(NamespacesList['resetPassword']);

	const dispatch = useAppDispatch();
	const userData = useSelector(getUser);

	const [isPasswordChanged, setIsPasswordChanged] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const {
		handleSubmit,
		register,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(resetPasswordSchema()),
	});

	const onSubmit = (data: any) => {
		const urlParams = new URLSearchParams(window.location.search);
		const id = urlParams.get('id');
		const token = urlParams.get('token');
		const { password } = data;

		if (id && token) {
			dispatch(resetPassword({ id, token, password })).then(() => {
				setIsPasswordChanged(true);
			});
		}
	};

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleClickShowConfirmPassword = () => {
		setShowConfirmPassword(!showConfirmPassword);
	};

	return (
		<>
			{isPasswordChanged ? (
				<ResetSuccess />
			) : (
				<Box
					className="rtl-able"
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						alignSelf: 'center',
						width: { xs: '100%', sm: '438px' },
						margin: '48px 0',
					}}
				>
					<Unlock />

					<Typography
						variant="h3"
						sx={{ fontSize: '24px', fontWeight: '700', marginTop: { xs: '16px', sm: '32px' } }}
					>
						{resetPasswordTranslate('text.title')}
					</Typography>

					<Typography sx={{ color: '#9FA6B3', marginTop: '8px', textAlign: 'center' }}>
						{resetPasswordTranslate('text.description')}
					</Typography>

					<form style={{ width: '100%' }} onSubmit={handleSubmit(onSubmit)}>
						<FormControl fullWidth error={!!errors.password}>
							<Typography sx={{ fontSize: '12px' }}>
								{resetPasswordTranslate('text.password')}
							</Typography>
							<TextField
								inputProps={{ style: { padding: '10px 12px', borderRadius: '8px' } }}
								autoComplete="password"
								type={showPassword ? 'text' : 'password'}
								placeholder={resetPasswordTranslate('placeholders.password') as string}
								sx={{
									height: '44px',
									'& .MuiOutlinedInput-root': {
										borderRadius: '8px',
									},
								}}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<Button
													disableRipple
													sx={{
														minWidth: 0,
														'&:hover': {
															backgroundColor: '#FFF',
														},
													}}
													onClick={handleClickShowPassword}
												>
													{showPassword ? <Eye /> : <EyeSlash />}
												</Button>
											</InputAdornment>
										),
									}}
								{...register('password')}
							/>
							<FormHelperText
								sx={{
									fontSize: '12px',
									textAlign: 'right',
									marginRight: '12px',
									marginTop: '0',
								}}
							>
								{errors.password?.message}
							</FormHelperText>
						</FormControl>

						<Grid mt={errors.password ? 0 : 2.4}>
							<FormControl fullWidth error={!!errors.confirmPassword}>
								<Typography sx={{ fontSize: '12px' }}>
									{resetPasswordTranslate('text.confirmPassword')}
								</Typography>
								<TextField
									inputProps={{ style: { padding: '10px 12px', borderRadius: '8px' } }}
									autoComplete="password"
									type={showConfirmPassword ? 'text' : 'password'}
									placeholder={resetPasswordTranslate('placeholders.password') as string}
									sx={{
										height: '44px',
										'& .MuiOutlinedInput-root': {
											borderRadius: '8px',
										},
									}}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<Button
													disableRipple
													sx={{
														minWidth: 0,
														'&:hover': {
															backgroundColor: '#FFF',
														},
													}}
													onClick={handleClickShowConfirmPassword}
												>
													{showConfirmPassword ? <Eye /> : <EyeSlash />}
												</Button>
											</InputAdornment>
										),
									}}
									{...register('confirmPassword')}
								/>
								<FormHelperText
									sx={{
										fontSize: '12px',
										textAlign: 'right',
										marginRight: '12px',
										marginTop: '0',
									}}
								>
									{errors.confirmPassword?.message}
								</FormHelperText>
							</FormControl>
						</Grid>

						{userData.loading ? (
							<Button
								sx={{
									height: '44px',
									width: { xs: '100%', sm: '438px' },
									borderRadius: '8px',
									marginTop: errors.password ? '20px' : '40px',
								}}
								variant="contained"
								color="primary"
							>
								<Oval
									height={24}
									width={24}
									color="#fff"
									wrapperStyle={{}}
									wrapperClass=""
									visible={true}
									ariaLabel="oval-loading"
									secondaryColor="#91B6FF"
									strokeWidth={5}
									strokeWidthSecondary={5}
								/>
							</Button>
						) : (
							<Button
								sx={{
									height: '44px',
									width: { xs: '100%', sm: '438px' },
									borderRadius: '8px',
									textTransform: 'none',
									fontSize: '16px',
									fontWeight: '700',
									marginTop: errors.password ? '20px' : '40px',
								}}
								type="submit"
								variant="contained"
								color="primary"
							>
								{resetPasswordTranslate('buttons.resetPassword')}
							</Button>
						)}
					</form>
				</Box>
			)}
		</>
	);
};
